import { useMemo } from 'react';

/**
 * Tabs that are filters over one list, each with its row count in the label — the Plan Entry
 * selector's Awaiting me / All tabs and the entry-access dialog's coverage tabs.
 */
export interface CountedTab<V extends string, R> {
  value: V;
  includes: (row: R) => boolean;
  /** Drop the tab while no row matches it, instead of offering an empty one. */
  hideWhenEmpty?: boolean;
}

/**
 * The counts, the tabs to show, the active tab and its rows. The chosen tab can lose its last row
 * on a refetch and be hidden; the active tab then falls back rather than select a tab that is not
 * there.
 */
export function useCountedTabs<V extends string, R>(rows: R[], tabs: CountedTab<V, R>[], chosen: V, fallback: V) {
  const counts = useMemo(
    () => new Map(tabs.map(s => [s.value, rows.filter(s.includes).length])),
    [rows, tabs],
  );
  const { visibleTabs, activeTab } = useMemo(() => {
    const visible = tabs.filter(s => !s.hideWhenEmpty || (counts.get(s.value) ?? 0) > 0);
    return { visibleTabs: visible, activeTab: visible.some(s => s.value === chosen) ? chosen : fallback };
  }, [tabs, counts, chosen, fallback]);
  const tabRows = useMemo(
    () => rows.filter(tabs.find(s => s.value === activeTab)?.includes ?? (() => true)),
    [rows, tabs, activeTab],
  );
  return { counts, visibleTabs, activeTab, tabRows };
}
